/**
 * API Origin Allowlist
 *
 * Validates that API base URLs point to trusted origins.
 * Used by both the widget loader and app runtime config.
 *
 * @security Fail closed — unknown origins are rejected.
 */

/**
 * Day 1: Allow Azure Container Apps FQDN as a trusted API origin.
 *
 * When api.matcora.com is live (ACA custom domain or API gateway),
 * set this to `false` for prd/stg and remove the ACA hostname check.
 *
 * Enforcement: add a CI guardrail that fails if ALLOW_ACA_HOSTS is
 * true in prd/stg after the Day-2 API domain rollout milestone.
 */
export const ALLOW_ACA_HOSTS = true;

type CanonicalEnvironment = 'dev' | 'dpt' | 'stg' | 'prd';

/**
 * Check if a URL points to a trusted API origin.
 *
 * Trusted origins:
 * - `api.matcora.com` — stable custom domain (all environments)
 * - `*.azurecontainerapps.io` — Day 1 temporary, gated by ALLOW_ACA_HOSTS
 *
 * @param url - The API base URL to validate
 * @param environment - Canonical environment (dev/dpt/stg/prd).
 *   dev/dpt are permissive (always allow ACA hosts).
 *   prd/stg respect the ALLOW_ACA_HOSTS flag.
 */
export function isAllowedApiOrigin(
  url: string,
  environment: CanonicalEnvironment = 'prd',
): boolean {
  try {
    const parsed = new URL(url);

    // Local development: allow http://localhost and http://127.0.0.1
    if (
      parsed.protocol === 'http:' &&
      (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')
    ) {
      return true;
    }

    if (parsed.protocol !== 'https:') return false;

    // Always allow stable API domain
    if (parsed.hostname === 'api.matcora.com') return true;

    // ACA hosts: always allowed in dev/dpt, flag-gated in prd/stg
    if (parsed.hostname.endsWith('.azurecontainerapps.io')) {
      if (environment === 'dev' || environment === 'dpt') return true;
      return ALLOW_ACA_HOSTS;
    }

    return false;
  } catch {
    return false;
  }
}
